import { useMemo } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface DateTimePickerProps {
  value: Date | null | undefined;
  onChange: (next: Date | null) => void;
  placeholder?: string;
  className?: string;
  /** Allow clearing — renders a "Clear" link inside the popover. */
  clearable?: boolean;
  disabled?: boolean;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toTimeString(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Date+time composite: shadcn Popover + Calendar for the date, and a native
 * `<input type="time">` for the time. The native time input on mobile
 * surfaces the OS picker — lighter than MUI's modal stepper.
 */
export function DateTimePicker({
  value,
  onChange,
  placeholder = 'Pick date & time',
  className,
  clearable,
  disabled,
}: DateTimePickerProps) {
  const display = useMemo(() => {
    if (!value) return null;
    return format(value, 'PPP') + ' · ' + toTimeString(value);
  }, [value]);

  const timeStr = value ? toTimeString(value) : '';

  const setDate = (next: Date | undefined) => {
    if (!next) return;
    const out = new Date(next);
    if (value) {
      out.setHours(value.getHours(), value.getMinutes(), 0, 0);
    } else {
      const now = new Date();
      out.setHours(now.getHours(), now.getMinutes(), 0, 0);
    }
    onChange(out);
  };

  const setTime = (next: string) => {
    if (!next) return;
    const [hRaw, mRaw] = next.split(':');
    const h = Number(hRaw);
    const m = Number(mRaw);
    if (Number.isNaN(h) || Number.isNaN(m)) return;
    const base = value ? new Date(value) : new Date();
    base.setHours(h, m, 0, 0);
    onChange(base);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {display ?? placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={setDate}
          autoFocus
        />
        <div className="border-t border-border p-3 space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Time</label>
          <Input
            type="time"
            value={timeStr}
            onChange={(e) => setTime(e.target.value)}
            disabled={disabled}
          />
          {clearable && value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => onChange(null)}
            >
              Clear
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DateTimePicker;
